import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IMG_PATH } from "utils/constants/imgPath";
import { Buttons, Icons, Inputs } from "components";
import SeatPriceSummary from "../SeatPriceSummary";

// รับเฉพาะรูป: ระบบตรวจสลิปอัตโนมัติอ่าน PDF ไม่ได้
const SLIP_TYPES = ["image/jpeg", "image/png"];
const MAX_SLIP_BYTES = 8 * 1024 * 1024;

// S4 ชำระเงิน: รหัสจองให้ใส่หมายเหตุโอน + บัญชีรับโอนและรูป QR ที่แอดมินอัปโหลด + แนบสลิป
// เลือกไฟล์แล้วตรวจกับ server ทันที ผ่านแล้วถึงกด "ต่อไป" ได้ ไม่ผ่านล้างไฟล์และปิดปุ่ม
// รูป QR เป็นของบัญชี (ไม่ได้ใส่ยอด) ลูกค้าต้องกรอกยอดเอง จึงบอกยอดที่ต้องโอนให้ชัด
const PaymentStep = ({ draft, info, countdownText, initialFile, onBack, onCheckSlip, onNext }) => {
  const { t } = useTranslation(["seat"]);
  const [_file, _setFile] = useState(initialFile || null);
  const [_slipError, _setSlipError] = useState(null);
  const [_isChecking, _setIsChecking] = useState(false);
  // ผ่านการตรวจแล้ว = มีสลิปที่ server เก็บไว้กับใบจองนี้ (ไฟล์ที่เพิ่งตรวจผ่าน หรือที่ตรวจไว้ก่อนออกจากหน้านี้)
  const isSlipPassed = !_isChecking && !_slipError && !!draft.has_pending_slip;
  const hasAccount = !!info.account_no;
  const qrUrl = info.account_qr_image ? IMG_PATH + info.account_qr_image : null;

  const _handleFile = async (files) => {
    const file = files && files[0];
    if (!file || _isChecking) return;
    if (!SLIP_TYPES.includes(file.type) || file.size > MAX_SLIP_BYTES) {
      _setFile(null);
      _setSlipError("The slip must be a JPG or PNG image no larger than 8 MB");
      return;
    }
    _setSlipError(null);
    _setFile(file);
    _setIsChecking(true);
    const errorKey = await onCheckSlip(file);
    _setIsChecking(false);
    if (errorKey) {
      _setFile(null);
      _setSlipError(errorKey);
    }
  };

  return (
    <div className="step_body">
      <div className="ss_heading">3.{t("Payment", { ns: "seat" })}</div>
      <SeatPriceSummary draft={draft} showTransferLabel />

      <div className="code_box">
        <div className="code_value">
          {t("Booking code", { ns: "seat" })}
          <span>{draft.code}</span>
        </div>
        <div className="code_hint">
          {t("Put this code in the transfer note.", { ns: "seat" })}
        </div>
        <div className="code_lock">
          {t("Seats are held for", { ns: "seat" })} <b>{countdownText}</b> {t("min", { ns: "seat" })}
          <br />
          {t("Please attach your transfer slip", { ns: "seat" })}
        </div>
      </div>

      <div className="pay_card">
        <div className="pay_card_head">
          <div className="pay_card_title">{t("Payment notification", { ns: "seat" })}</div>
          <div className="pay_card_close" onClick={onBack}>
            <Icons.Close width={14} height={14} color="#4B1E78" />
          </div>
        </div>
        <div className="pay_detail_title">{t("Payment details", { ns: "seat" })}</div>
        {hasAccount ? (
          <>
            <div className="pay_account">
              <div className="pay_account_label">{t("Account number", { ns: "seat" })}</div>
              <div>
                <div>{info.account_no}</div>
                {info.account_name && <div>{info.account_name}</div>}
              </div>
            </div>
            {qrUrl && (
              <>
                <img className="account_qr" src={qrUrl} alt="QR" />
                <a className="pay_download" href={qrUrl} target="_blank" rel="noopener noreferrer" download>
                  {t("Download QR Code", { ns: "seat" })}
                </a>
              </>
            )}
          </>
        ) : (
          <div className="closed_block">{t("Payment account is not set up yet. Please contact staff.", { ns: "seat" })}</div>
        )}
        <div className="pay_upload">
          <div className="pay_upload_label">{t("Upload transfer slip", { ns: "seat" })}</div>
          <Inputs.Upload
            acceptType="cover"
            value={_file ? [_file] : []}
            placeholder={t("Upload file", { ns: "seat" })}
            onChange={_handleFile}
            onReject={() => {
              _setFile(null);
              _setSlipError("The slip must be a JPG or PNG image no larger than 8 MB");
            }}
          />
          {_isChecking && <div className="pay_upload_note">{t("Checking the slip...", { ns: "seat" })}</div>}
          {isSlipPassed && <div className="pay_upload_ok">{t("Slip attached successfully", { ns: "seat" })}</div>}
          {_slipError && <div className="pay_upload_error">{t(_slipError, { ns: "seat" })}</div>}
        </div>
      </div>

      <div className="btn_row">
        <div>
          <Buttons.BgStandard theme_white_purple label={t("Back", { ns: "seat" })} onClick={onBack} />
        </div>
        <div>
          <Buttons.BgStandard label={t("Next", { ns: "seat" })} disabled={!isSlipPassed || !hasAccount} onClick={onNext} />
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;
